import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  HelpCircle,
  Flag,
  GitBranch,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  Route,
  ShieldAlert,
  AlertTriangle,
  UserRound,
  Wrench,
} from "lucide-react";

import {
  type RuntimeEventLike,
  type Severity,
  type StreamStatus,
  EVENT_FILTERS,
  STREAM_MESSAGE,
  describeEvent,
  formatDuration,
  groupForDisplay,
  nextStreamState,
  summarizeDetail,
  summarizeStream,
} from "./runtimeEventsViewModel";
import { type RuntimeHealth, describeRuntimeMode } from "./runtimeModeViewModel";

/**
 * Story SF-CHAT-5.2 — painel lateral de eventos do runtime.
 *
 * Herda a superfície da conversa: mesmas famílias de cor, mesmo raio, mesmos
 * ícones da `lucide-react`. Uma adição local não inventa identidade.
 *
 * O fluxo da Story 5.1 entrega o buffer do turno e encerra a conexão, então
 * "ao vivo" aqui é **amostragem de um segundo**, não empurrão do servidor. Está
 * dito assim na story em vez de chamar de streaming o que não é.
 */

const ICONS: Record<string, typeof MessageSquare> = {
  MessageSquare,
  GitBranch,
  Route,
  Wrench,
  CheckCircle2,
  ShieldAlert,
  UserRound,
  AlertTriangle,
  Flag,
  HelpCircle,
};

/** Tom por severidade. A cor acompanha o rótulo textual; nunca carrega sozinha. */
const SEVERITY_TONE: Record<Severity, { row: string; icon: string; chip: string }> = {
  sucesso: {
    row: "border-slate-200 bg-white",
    icon: "text-emerald-700",
    chip: "bg-emerald-50 text-emerald-900 border-emerald-200",
  },
  recusa: {
    row: "border-amber-200 bg-amber-50",
    icon: "text-amber-800",
    chip: "bg-amber-100 text-amber-900 border-amber-300",
  },
  erro: {
    row: "border-red-200 bg-red-50",
    icon: "text-red-800",
    chip: "bg-red-100 text-red-900 border-red-300",
  },
  neutro: {
    row: "border-slate-200 bg-white",
    icon: "text-slate-600",
    chip: "bg-slate-100 text-slate-800 border-slate-300",
  },
};

const INTERVALO_DE_AMOSTRAGEM_MS = 1000;

interface RuntimeEventsPanelProps {
  sessionId: string;
  correlationId: string;
  open: boolean;
  onToggle: (open: boolean) => void;
  /** Turno selecionado na conversa; destaca o grupo correspondente (AC #3). */
  selectedTurnId?: string | null;
  /** Selecionar um turno no painel destaca a mensagem na conversa (AC #3). */
  onSelectTurn?: (turnId: string | null) => void;
  /** Saúde do runtime, para declarar o modo vigente (AC #3 da Story 6.2). */
  runtime?: RuntimeHealth | null;
}

function horaDe(iso: unknown): string {
  if (typeof iso !== "string") return "";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "";
  return data.toLocaleTimeString("pt-BR", { hour12: false });
}

export function RuntimeEventsPanel({
  sessionId,
  correlationId,
  open,
  onToggle,
  selectedTurnId = null,
  onSelectTurn,
  runtime = null,
}: RuntimeEventsPanelProps) {
  const [eventos, setEventos] = useState<RuntimeEventLike[]>([]);
  const [filtro, setFiltro] = useState("todos");
  const modoVigente = describeRuntimeMode(runtime);
  const [status, setStatus] = useState<StreamStatus>({ state: "idle", attempt: 0 });
  const statusRef = useRef(status);
  statusRef.current = status;

  const buscar = useCallback(
    async (sinal: AbortSignal) => {
      const resposta = await fetch("/api/chat/events", {
        headers: { "X-Chat-Session-Id": sessionId, "X-Chat-Correlation-Id": correlationId },
        signal: sinal,
      });
      if (resposta.status === 503) {
        setStatus({ state: "down", attempt: 0 });
        return null;
      }
      if (!resposta.ok) throw new Error(String(resposta.status));
      const texto = await resposta.text();
      return texto
        .split("\n")
        .filter((linha) => linha.startsWith("data: ") && linha.trim() !== "data: [DONE]")
        .map((linha) => {
          try {
            return JSON.parse(linha.slice("data: ".length)) as RuntimeEventLike;
          } catch {
            return null;
          }
        })
        .filter((evento): evento is RuntimeEventLike => evento !== null);
    },
    [sessionId, correlationId],
  );

  useEffect(() => {
    let cancelado = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controle = new AbortController();

    const ciclo = async () => {
      if (cancelado) return;
      try {
        const recebidos = await buscar(controle.signal);
        if (cancelado || recebidos === null) return;
        setEventos(recebidos);
        setStatus((atual) => nextStreamState(atual, { type: "events", count: recebidos.length }));
        timer = setTimeout(ciclo, INTERVALO_DE_AMOSTRAGEM_MS);
      } catch (erro) {
        if (cancelado || (erro instanceof DOMException && erro.name === "AbortError")) return;
        const proximo = nextStreamState(statusRef.current, { type: "failure" });
        setStatus(proximo);
        if (proximo.state === "down") return;
        timer = setTimeout(ciclo, proximo.retryDelayMs ?? INTERVALO_DE_AMOSTRAGEM_MS);
      }
    };

    setStatus((atual) => nextStreamState(atual, { type: "connect" }));
    void ciclo();

    return () => {
      cancelado = true;
      controle.abort();
      if (timer) clearTimeout(timer);
    };
  }, [buscar]);

  // Os grupos exibidos trazem as linhas do filtro e o **estado real** do turno:
  // o filtro esconde linhas, não reescreve fatos.
  const turnos = useMemo(() => groupForDisplay(eventos, filtro), [eventos, filtro]);
  const numeros = useMemo(() => summarizeStream(eventos, filtro), [eventos, filtro]);
  const errosEscondidos = numeros.hiddenErrors;

  const resumo = useMemo(() => {
    if (status.state === "down" || status.state === "reconnecting") return STREAM_MESSAGE[status.state];
    if (numeros.totalEvents === 0) {
      return "Nenhum evento ainda. Envie uma mensagem para ver o agente trabalhar.";
    }
    // Os totais são do fluxo inteiro. O recorte do filtro é dito à parte, senão
    // "1 turno" com um filtro ligado faria parecer que a conversa só teve um.
    const partes = [`${numeros.totalEvents} evento${numeros.totalEvents === 1 ? "" : "s"}`];
    partes.push(`${numeros.totalTurns} turno${numeros.totalTurns === 1 ? "" : "s"}`);
    if (numeros.inFlight) partes.push("turno em andamento");
    if (numeros.turnsWithError > 0) partes.push(`${numeros.turnsWithError} com erro`);
    if (numeros.filtered) partes.push(`${numeros.visibleEvents} à vista`);
    return `${partes.join(" · ")}.`;
  }, [status.state, numeros]);

  return (
    <aside
      aria-label="Eventos do runtime"
      className={`flex min-w-0 flex-col self-start rounded-lg border border-slate-200 bg-white shadow-sm lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] ${
        open ? "" : "lg:w-64"
      }`}
    >
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-900">Eventos do runtime</h2>
          {/* O modo vigente fica ao lado dos eventos: julgar comportamento sem
              saber se a origem é fixture ou API oficial não significa nada. */}
          <p className="mt-0.5 text-xs">
            <span
              className={`rounded border px-1 py-px font-medium ${
                modoVigente.live
                  ? "border-red-300 bg-red-50 text-red-900"
                  : modoVigente.unavailable || modoVigente.unknown
                  ? "border-slate-300 bg-slate-100 text-slate-800"
                  : "border-emerald-300 bg-emerald-50 text-emerald-900"
              }`}
            >
              {modoVigente.label}
            </span>
          </p>
          <p role="status" aria-live="polite" className="mt-0.5 text-xs text-slate-600">
            {resumo}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onToggle(!open)}
          aria-expanded={open}
          aria-controls="lista-de-eventos"
          className="flex shrink-0 items-center gap-1.5 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
        >
          {open ? (
            <PanelRightClose className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <PanelRightOpen className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {/* O controle nomeia a ação. Só ícone, o painel recolhido mostra uma
              contagem de eventos e nenhum caminho óbvio para vê-los. */}
          {open ? "Ocultar" : "Mostrar"}
        </button>
      </div>

      {open && (
        <div
          role="group"
          aria-label="Filtrar eventos por tipo"
          className="flex flex-wrap gap-1 border-b border-slate-200 px-3 py-2"
        >
          {EVENT_FILTERS.map((opcao) => {
            const ativo = opcao.key === filtro;
            return (
              <button
                key={opcao.key}
                type="button"
                onClick={() => setFiltro(opcao.key)}
                aria-pressed={ativo}
                className={`rounded border px-2 py-0.5 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 ${
                  ativo
                    ? "border-blue-700 bg-blue-700 font-semibold text-white"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                {opcao.label}
              </button>
            );
          })}
        </div>
      )}

      {open && (
        <div
          id="lista-de-eventos"
          role="log"
          aria-live="polite"
          aria-label="Eventos do turno"
          tabIndex={0}
          className="painel-eventos-lista min-h-0 flex-1 overflow-y-auto p-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-700"
        >
          {status.state === "down" && (
            <p className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">
              {STREAM_MESSAGE.down}
            </p>
          )}

          {status.state === "reconnecting" && (
            <p className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              {STREAM_MESSAGE.reconnecting}
            </p>
          )}

          {numeros.totalEvents === 0 && status.state !== "down" && (
            <p className="p-3 text-xs text-slate-600">
              {status.state === "connecting"
                ? STREAM_MESSAGE.connecting
                : "Nenhum evento ainda. Envie uma mensagem para ver a rota escolhida, a ferramenta chamada e o que voltou."}
            </p>
          )}

          {errosEscondidos > 0 && (
            /* AC #4: esconder erro sem avisar é pior que não filtrar — quem está
               validando concluiria que o turno correu limpo. */
            <p className="mb-3 flex items-center gap-1.5 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                {errosEscondidos === 1
                  ? "1 erro está oculto por este filtro."
                  : `${errosEscondidos} erros estão ocultos por este filtro.`}{" "}
                <button
                  type="button"
                  onClick={() => setFiltro("erro")}
                  className="font-semibold underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700"
                >
                  Ver os erros
                </button>
              </span>
            </p>
          )}

          <ol className="space-y-4">
            {turnos.map((turno, indice) => {
              const selecionado = selectedTurnId != null && selectedTurnId === turno.turnId;
              return (
              <li
                key={turno.turnId}
                data-turno={turno.turnId}
                className={
                  selecionado
                    ? "rounded-md bg-blue-50 p-1.5 ring-2 ring-blue-700 ring-offset-1"
                    : ""
                }
              >
                <div className="mb-1.5 flex items-baseline justify-between gap-2 text-xs">
                  <h3 className="min-w-0 truncate whitespace-nowrap font-semibold text-slate-800">
                    <button
                      type="button"
                      onClick={() => onSelectTurn?.(selecionado ? null : turno.turnId)}
                      aria-pressed={selecionado}
                      className="rounded underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
                    >
                      {turno.orphan ? "Eventos sem turno" : `Turno ${indice + 1}`}
                    </button>
                    {turno.finished ? "" : " · em andamento"}
                    {selecionado && <span className="sr-only"> (selecionado na conversa)</span>}
                  </h3>
                  <span className="shrink-0 whitespace-nowrap tabular-nums text-slate-600">
                    {formatDuration(turno.totalDurationMs) ?? ""}
                  </span>
                </div>
                <ol className="space-y-1.5">
                  {turno.events.map((evento, posicao) => {
                    const descricao = describeEvent(evento);
                    const tom = SEVERITY_TONE[descricao.severity];
                    const Icone = ICONS[descricao.icon] ?? HelpCircle;
                    const duracao = formatDuration(evento.duration_ms);
                    const detalhes = summarizeDetail(evento.detail);
                    return (
                      <li
                        key={`${turno.turnId}-${posicao}`}
                        className={`motion-safe:animate-[evento-entra_240ms_cubic-bezier(0.16,1,0.3,1)_both] motion-reduce:animate-none rounded border p-2 ${tom.row}`}
                      >
                        <div className="flex items-start gap-2">
                          <Icone className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${tom.icon}`} aria-hidden="true" />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                              <span className="text-xs font-medium text-slate-900">{descricao.label}</span>
                              {descricao.severity !== "neutro" && (
                                /* A etiqueta existe para distinguir sucesso, recusa
                                   e erro sem depender de cor. Em passo neutro ela
                                   aparecia em quase toda linha e virava ruído,
                                   enfraquecendo justamente o sinal que importa. */
                                <span
                                  className={`rounded border px-1 py-px text-[0.65rem] font-semibold uppercase tracking-wide ${tom.chip}`}
                                >
                                  {descricao.severityLabel}
                                </span>
                              )}
                              <span className="tabular-nums text-[0.65rem] text-slate-600">
                                {horaDe(evento.occurred_at)}
                                {duracao ? ` · ${duracao}` : ""}
                              </span>
                            </div>
                            {detalhes.length > 0 && (
                              <dl className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[0.7rem]">
                                {detalhes.map((par) => (
                                  <div key={par.key} className="flex min-w-0 max-w-full gap-1">
                                    <dt className="shrink-0 text-slate-600">{par.label}</dt>
                                    {/* `break-words` não quebra identificador com
                                        underscore — `payables_next_seven_days` vazava
                                        para fora do painel e era cortado. `anywhere`
                                        prefere a fronteira de palavra e só parte o
                                        token quando não há alternativa. */}
                                    <dd className="min-w-0 [overflow-wrap:anywhere] font-medium text-slate-800">
                                      {par.value}
                                    </dd>
                                  </div>
                                ))}
                              </dl>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </li>
              );
            })}
          </ol>
        </div>
      )}
    </aside>
  );
}
